// COGS explorer — vendor/model price catalog.
//
// LLM / embedding / TTS / Perplexity rates come from `pricing.generated.ts`, which
// is refreshed from LiteLLM's MIT-licensed pricing dataset via `npm run
// update-prices` (data only — the LiteLLM package/proxy is never installed or run;
// see scripts/update-prices.mjs). Apollo enrichment and Stripe processing aren't in
// any LLM price feed, so they're hand-maintained below. Every number is editable at
// runtime — this file only seeds the defaults.

import { GENERATED_MODELS, PRICING_META } from './pricing.generated';

export { PRICING_META };

export type RateKind =
  | 'llm' // per-1M-token input/output (+ optional grounding surcharge)
  | 'embedding' // per-1M-token input only
  | 'per_call' // flat fee per invocation
  | 'per_char' // per-1k-characters
  | 'payment' // percent + fixed (Stripe)
  | 'subscription'; // monthly plan + included credits + overage (Apollo, ElevenLabs)

export interface ModelRate {
  id: string;
  vendor: string;
  label: string;
  kind: RateKind;
  /** From the live feed ('current') vs. a hand-tuned estimate ('estimate'). */
  confidence: 'current' | 'estimate';
  // llm / embedding
  inputPerM?: number;
  outputPerM?: number;
  /** Google Search grounding surcharge, USD per grounded request. */
  groundingPerCall?: number;
  // per_call
  perCall?: number;
  // per_char
  perThousandChars?: number;
  // payment
  percentFee?: number;
  fixedFee?: number;
  // subscription — a monthly plan (monthlyCost) that includes `includedCredits`
  // credits; raw usage (calls or characters) converts to credits via
  // `creditsPerUnit`; usage beyond the allotment bills at `overagePer1kCredits`.
  // Per-unit COGS uses the blended plan rate (monthlyCost / includedCredits);
  // platform monthly cost = monthlyCost + overage on credits over the allotment
  // (see compute.subscriptionMonthlyCost).
  monthlyCost?: number;
  includedCredits?: number;
  overagePer1kCredits?: number;
  creditsPerUnit?: number; // credits consumed per raw usage unit
  unit?: 'call' | 'char'; // what the raw usage measures
  note?: string;
}

// ── Hand-maintained vendors (no LLM price feed covers these) ──────────────────

export const HAND_MODELS: ModelRate[] = [
  {
    id: 'apollo-enrich',
    vendor: 'Apollo',
    label: 'Apollo (Pro plan)',
    kind: 'subscription',
    confidence: 'current',
    monthlyCost: 99,
    includedCredits: 10000,
    overagePer1kCredits: 9.9, // ≈ blended rate; Apollo overage is plan-dependent — verify
    creditsPerUnit: 1, // 1 credit per enrichment call (searches are typically free)
    unit: 'call',
    note: '$99/mo → 10,000 credits. Blended $0.0099/credit. 1 credit per enrichment; org/people searches are usually free — set 0 if counting only enrichments.',
  },
  {
    id: 'eleven-multilingual-v2',
    vendor: 'ElevenLabs',
    label: 'ElevenLabs (Pro plan)',
    kind: 'subscription',
    confidence: 'current',
    monthlyCost: 99,
    includedCredits: 600000,
    overagePer1kCredits: 0.24, // Pro-tier additional-credit rate — verify against your invoice
    creditsPerUnit: 0.5, // Turbo/Flash = 0.5 credit/char; Multilingual v2 = 1.0
    unit: 'char',
    note: '$99/mo → 600,000 credits. Blended $0.000165/credit. Turbo v2.5 = 0.5 credit/char (set 1.0 for Multilingual v2).',
  },
  {
    id: 'stripe',
    vendor: 'Stripe',
    label: 'Stripe processing',
    kind: 'payment',
    confidence: 'current',
    percentFee: 0.029,
    fixedFee: 0.3,
    note: 'Standard 2.9% + $0.30 per successful charge.',
  },
];

// Feed-sourced models first, then the hand-maintained vendors.
export const DEFAULT_MODELS: ModelRate[] = [...GENERATED_MODELS, ...HAND_MODELS];

export function modelsById(models: ModelRate[]): Record<string, ModelRate> {
  const map: Record<string, ModelRate> = {};
  for (const m of models) map[m.id] = m;
  return map;
}
